/**
 * ID Generation Utilities
 *
 * Consolidated ID generation for consistent usage across the codebase.
 * Provides both simple DOM IDs and cryptographically secure UUIDs.
 *
 * `crypto.randomUUID` is only defined in a secure context (HTTPS, localhost,
 * or 127.0.0.1) and only from Safari 15.4. Loading a dev build from a LAN IP
 * over plain HTTP — the normal way to check a layout on a real phone — leaves
 * it `undefined`, and an unguarded call threw before the first component
 * finished mounting (#484). Every entry point here degrades instead.
 */
/** Hex alphabet for manual UUID assembly. */
const HEX = '0123456789abcdef';
/** Counter backing the last-resort DOM id path. */
let sequence = 0;
/**
 * Reads the platform crypto object without assuming it exists.
 * Workers, browsers, and Bun all expose it globally; a non-secure context may
 * expose the object while omitting `randomUUID`.
 */
function getCrypto() {
    return typeof globalThis.crypto === 'object' ? (globalThis.crypto ?? undefined) : undefined;
}
/**
 * Builds an RFC 4122 version 4 UUID from CSPRNG bytes.
 *
 * `getRandomValues` has no secure-context requirement and predates the
 * baseline everywhere, so this path is available exactly when `randomUUID`
 * is not.
 */
function randomBytes(cryptoObj, length) {
    // Uint8Array.from rather than `new Uint8Array(length)`: the length-only
    // constructor reads as a sparse allocation, and the elements are overwritten
    // by getRandomValues immediately anyway.
    const bytes = Uint8Array.from({ length }, () => 0);
    cryptoObj.getRandomValues(bytes);
    return bytes;
}
/** Renders bytes as lowercase hex. */
function toHex(bytes) {
    let hex = '';
    for (let index = 0; index < bytes.length; index += 1) {
        const byte = bytes[index] ?? 0;
        hex += HEX[byte >> 4];
        hex += HEX[byte & 0x0f];
    }
    return hex;
}
function uuidFromRandomValues(cryptoObj) {
    const bytes = randomBytes(cryptoObj, 16);
    // Version 4, variant 10xx — the two fields randomUUID would have set.
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = toHex(bytes);
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
/**
 * Generates a short random alphanumeric ID.
 * Suitable for DOM element IDs, CSS class names, and non-security-critical identifiers.
 *
 * Never throws: a DOM id has no randomness requirement, so when no crypto
 * source is reachable it falls back to a monotonic counter. Uniqueness within
 * the document is what matters here, and the counter provides it.
 *
 * @example
 * generateId() // => "k7x9m2p4q"
 */
export function generateId() {
    const cryptoObj = getCrypto();
    if (cryptoObj?.getRandomValues) {
        return toHex(randomBytes(cryptoObj, 5)).slice(0, 9);
    }
    sequence += 1;
    return `${Date.now().toString(36)}${sequence.toString(36)}`.slice(-9);
}
/**
 * Generates a cryptographically secure UUID v4.
 * Use for session IDs, tokens, and security-sensitive identifiers.
 *
 * Works in both browser (Web Crypto API) and server (Cloudflare Workers) environments.
 *
 * Both paths are CSPRNG-backed. If neither is reachable this throws rather
 * than degrading: a caller asking for a security-sensitive identifier must not
 * silently receive a predictable one.
 *
 * @throws {Error} When no Web Crypto source is available.
 *
 * @example
 * generateUUID() // => "550e8400-e29b-41d4-a716-446655440000"
 */
export function generateUUID() {
    const cryptoObj = getCrypto();
    if (typeof cryptoObj?.randomUUID === 'function') {
        return cryptoObj.randomUUID();
    }
    if (typeof cryptoObj?.getRandomValues === 'function') {
        return uuidFromRandomValues(cryptoObj);
    }
    throw new Error('generateUUID() requires Web Crypto. No crypto.randomUUID or crypto.getRandomValues is available in this environment.');
}
//# sourceMappingURL=id.js.map